<template>
  <transition
    name="el-zoom-in-top"
    @after-enter="handleEnter"
    @after-leave="handleLeave"
  >
    <div
      v-show="visible"
      :class="[
        {
          'has-sidebar': $slots.sidebar || shortcuts,
          'has-time': showTime
        },
        popperClass
      ]"
      class="rt-xpc-date-picker el-picker-panel el-date-picker el-popper"
    >
      <div class="el-picker-panel__body-wrapper">
        <div
          v-if="$slots['date-header']"
          class=" rt-xpc-time-panel-slot rt-xpc-time-panel-slot__header"
        >
          <slot name="date-header">
          </slot>
        </div>

        <div class="el-picker-panel__body">
          <div
            v-if="showTime"
            class="el-date-picker__time-header"
          >
            <span class="el-date-picker__editor-wrap">
              <el-input
                ref="dateInput"
                :placeholder="t('el.datepicker.selectDate')"
                :value="visibleDate"
                size="small"
                @input="val => (userInputDate = val)"
                @change="handleVisibleDateChange"
              />
            </span>
            <span
              v-clickoutside="handleTimePickClose"
              class="el-date-picker__editor-wrap"
            >
              <el-input
                ref="input"
                :placeholder="t('el.datepicker.selectTime')"
                :value="visibleTime"
                size="small"
                @click="timePickerVisible = true"
                @focus="handleFocus"
                @blur="handleBlur"
                @input="val => (userInputTime = val)"
                @change="handleVisibleTimeChange"
              />
              <time-picker
                ref="timepicker"
                :time-arrow-control="arrowControl"
                :visible="timePickerVisible"
                :stepper="stepper"
                @time-confirm="handleTimeConfirm"
                @time-cancel="handleTimeCancel"
                @mounted="proxyTimePickerDataProperties"
              >
              </time-picker>
            </span>
          </div>
          <div
            v-show="currentView !== 'time'"
            :class="{
              'el-date-picker__header--bordered':
                currentView === 'year' || currentView === 'month'
            }"
            class="el-date-picker__header"
          >
            <button
              :aria-label="t(`el.datepicker.prevYear`)"
              class="el-picker-panel__icon-btn el-date-picker__prev-btn el-icon-d-arrow-left"
              type="button"
              @click="prevYear"
            ></button>
            <button
              v-show="currentView === 'date'"
              :aria-label="t(`el.datepicker.prevMonth`)"
              class="el-picker-panel__icon-btn el-date-picker__prev-btn el-icon-arrow-left"
              type="button"
              @click="prevMonth"
            ></button>
            <span
              role="button"
              class="el-date-picker__header-label"
              @click="showYearPicker"
            >{{ yearLabel }}</span>
            <span
              v-show="currentView === 'date'"
              :class="{ active: currentView === 'month' }"
              class="el-date-picker__header-label"
              role="button"
              @click="showMonthPicker"
            >{{ t(`el.datepicker.month${month + 1}`) }}</span>
            <button
              v-show="currentView === 'date'"
              :aria-label="t(`el.datepicker.nextMonth`)"
              class="el-picker-panel__icon-btn el-date-picker__next-btn el-icon-arrow-right"
              type="button"
              @click="nextMonth"
            ></button>
            <button
              :aria-label="t(`el.datepicker.nextYear`)"
              type="button"
              class="el-picker-panel__icon-btn el-date-picker__next-btn el-icon-d-arrow-right"
              @click="nextYear"
            ></button>
          </div>

          <div class="el-picker-panel__content">
            <date-table
              v-show="currentView === 'date'"
              :selection-mode="selectionMode"
              :first-day-of-week="firstDayOfWeek"
              :value="date"
              :default-value="defaultValue ? new Date(defaultValue) : null"
              :date="keyboardDate"
              :cell-class-name="cellClassName"
              :disabled-date="disabledDate"
              :keyboard-date="keyboardDate"
              :old-value="oldValue"
              @pick="handleDatePick"
            >
            </date-table>
            <year-table
              v-show="currentView === 'year'"
              :value="date"
              :default-value="defaultValue ? new Date(defaultValue) : null"
              :date="keyboardDate"
              :disabled-date="disabledDate"
              :keyboard-date="keyboardDate"
              :old-value="oldValue"
              @pick="handleYearPick"
            >
            </year-table>
            <month-table
              v-show="currentView === 'month'"
              :value="date"
              :default-value="defaultValue ? new Date(defaultValue) : null"
              :date="keyboardDate"
              :disabled-date="disabledDate"
              :keyboard-date="keyboardDate"
              :old-value="oldValue"
              @pick="handleMonthPick"
            >
            </month-table>
          </div>
        </div>
        <slot
          name="sidebar"
          class="rt-xpc-picker-panel__sidebar el-picker-panel__sidebar"
        ></slot>
        <div
          v-if="shortcuts"
          :class="'rt-xpc-picker-panel__sidebar-' + footerAlign"
          class="rt-xpc-picker-panel__sidebar  el-picker-panel__sidebar"
        >
          <rt-button
            v-for="(shortcut, key) in shortcuts"
            :key="key"
            text
            type="plain"
            class="el-picker-panel__shortcut"
            @click="handleShortcutClick(shortcut)"
          >
            {{ shortcut.text }}
          </rt-button>
        </div>
      </div>

      <div
        v-show="footerVisible && currentView === 'date'"
        class="rt-xpc-picker-panel__footer el-picker-panel__footer"
      >
        <div class="rt-xpc-date-panel__now">
          <rt-button
            v-show="selectionMode !== 'dates'"
            text
            background
            class="el-picker-panel__link-btn"
            @click="changeToNow"
          >
            {{ t('el.datepicker.now') }}
          </rt-button>
        </div>
        <div class="rt-xpc-date-panel__btn">
          <rt-button
            text
            @click="handleCancel"
          >
            {{ t('el.datepicker.cancel') }}
          </rt-button>
          <rt-button
            text
            type="plain"
            class="el-picker-panel__link-btn"
            @click="confirm"
          >
            {{ t('el.datepicker.confirm') }}
          </rt-button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script type="text/babel">
import {
  formatDate,
  parseDate,
  getWeekNumber,
  isDate,
  modifyDate,
  modifyTime,
  modifyWithTimeString,
  clearMilliseconds,
  clearTime,
  prevYear,
  nextYear,
  prevMonth,
  nextMonth,
  changeYearMonthAndClampDate,
  extractDateFormat,
  extractTimeFormat,
  timeWithinRange
} from 'element-ui/lib/utils/date-util';
import Clickoutside from 'element-ui/lib/utils/clickoutside';
import Locale from 'element-ui/lib/mixins/locale';
import ElInput from '../../input/src/input.vue';
import TimePicker from './time';
import YearTable from '../basic/year-table';
import MonthTable from '../basic/month-table';
import DateTable from '../basic/date-table';
import Keydown from '../keydown.js';
import RtButton from 'vue-xpc/src/components/button/rtButton.vue';

const POS_TIME_MINUTE = {
  start: 3,
  end: 5
};

const POS_TIME_SECOND = {
  start: 6,
  end: 8
};

export default {
  directives: { Clickoutside },

  components: {
    TimePicker,
    YearTable,
    MonthTable,
    DateTable,
    ElInput,
    RtButton
  },
  mixins: [Locale, Keydown],

  props: {
    footerAlign: {
      type: String,
      default: 'center'
    },
    // 分钟 / 秒跨度
    stepper: [Number, Boolean],
    // 点击 <此刻> 不关闭 picker
    clickNowShowPicker: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      popperClass: '',
      date: new Date(),
      oldValue: '', // 输入框值
      keyboardDate: new Date(),
      value: '',
      defaultValue: null, // use getDefaultValue() for time computation
      defaultTime: null,
      showTime: false,
      selectionMode: 'day',
      shortcuts: '',
      visible: false,
      currentView: 'date',
      disabledDate: '',
      cellClassName: '',
      selectableRange: [],
      firstDayOfWeek: 7,
      showWeekNumber: false,
      timePickerVisible: false,
      format: '',
      arrowControl: false,
      userInputDate: null,
      userInputTime: null,
      timeInputFocus: false, // time input 聚焦
      timeInputBlur: false, // time input 失焦
      collapseTimePicker: false, // 是否允许收起 time picker
      collapseDatePicker: false, // 是否允许收起 date picker
      isEnterKeyboard: false
    };
  },

  computed: {
    year () {
      return this.keyboardDate && this.keyboardDate.getFullYear();
    },

    month () {
      return this.keyboardDate && this.keyboardDate.getMonth();
    },

    week () {
      return getWeekNumber(this.date);
    },

    monthDate () {
      return this.date.getDate();
    },

    footerVisible () {
      return this.showTime || this.selectionMode === 'dates';
    },

    _computedStepper () {
      if (typeof this.stepper === 'number') {
        return this.stepper;
      }
      return this.stepper ? 10 : 0;
    },

    visibleTime () {
      if (this.userInputTime !== null) {
        return this.userInputTime;
      } else {
        let value = formatDate(
          this.date || this.getDefaultValue() || this.defaultValue,
          this.timeFormat
        );
        if (this._computedStepper && value) {
          let startIndex = this._getValueIndex(value).startIndex;
          let endIndex = this._getValueIndex(value).endIndex;
          // 当前时间 分钟 / 秒
          let currentTime = +value.slice(startIndex, endIndex);
          // 跨度值, 比如：[00, 15, 30, 45]
          let stepValue =
            this._computedStepper *
            Math.floor(currentTime / this._computedStepper);
          return (
            value.slice(0, startIndex) +
            (stepValue < 10 ? '0' + stepValue : stepValue)
          );
        } else {
          return value;
        }
      }
    },

    visibleDate () {
      if (this.userInputDate !== null) {
        return this.userInputDate;
      } else {
        return formatDate(
          this.oldValue || this.getDefaultValue() || this.defaultValue,
          this.dateFormat
        );
      }
    },

    yearLabel () {
      const yearTranslation = this.t('el.datepicker.year');
      if (this.currentView === 'year') {
        const startYear = Math.floor(this.year / 10) * 10;
        if (yearTranslation) {
          return (
            startYear +
            ' ' +
            yearTranslation +
            ' - ' +
            (startYear + 9) +
            ' ' +
            yearTranslation
          );
        }
        return startYear + ' - ' + (startYear + 9);
      }
      return this.year + ' ' + yearTranslation;
    },

    timeFormat () {
      if (this.format) {
        return extractTimeFormat(this.format);
      } else {
        return 'HH:mm:ss';
      }
    },

    dateFormat () {
      if (this.format) {
        return extractDateFormat(this.format);
      } else {
        return 'yyyy-MM-dd';
      }
    },

    refInput () {
      if (this.$refs.input.$el) {
        return [].slice.call(this.$refs.input.$el.querySelectorAll('input'));
      }
      return [];
    },

    refDateInput () {
      if (this.$refs.dateInput.$el) {
        return [].slice.call(
          this.$refs.dateInput.$el.querySelectorAll('input')
        );
      }
      return [];
    },

    currentDate () {
      return new Date();
    }
  },

  watch: {
    showTime (val) {
      /* istanbul ignore if */
      if (!val) return;
      this.$nextTick(_ => {
        const inputElm = this.$refs.input.$el;
        if (inputElm) {
          this.pickerWidth = inputElm.getBoundingClientRect().width + 10;
        }
      });
    },

    value (val) {
      if (this.selectionMode === 'dates' && this.value) return;
      if (isDate(val)) {
        this.date = new Date(val);
      } else {
        this.date = this.getDefaultValue();
      }
    },

    date (val, oldValue) {
      if (
        formatDate(val, this.dateFormat) !==
          formatDate(oldValue, this.dateFormat) ||
        formatDate(val, this.timeFormat) !==
          formatDate(oldValue, this.timeFormat)
      ) {
        this.keyboardDate = val;
      }
    },

    visible (val) {
      if (val) {
        this.date = this.oldValue = this.value || new Date();
        this.keyboardDate = this.value || this.currentDate;
        this.isEnterKeyboard = true;
        this.$nextTick(() => {
          if (this.type === 'datetime') {
            this.refDateInput &&
              this.refDateInput.forEach(input => {
                input.focus();
              });
          }
        });
      } else {
        this.isEnterKeyboard = false;
        this.collapseDatePicker = false;
        this.collapseTimePicker = false;
        this.timeInputBlur = false;
        if (this.value) {
          this.date = this.value;
        }
      }
    },

    defaultValue (val) {
      if (!isDate(this.value)) {
        this.date = val ? new Date(val) : new Date();
      }
    },

    timePickerVisible (val) {
      if (val) {
        this.$nextTick(() => this.$refs.timepicker.adjustSpinners());
      } else {
        this.timeInputFocus = false;
      }
    },

    selectionMode (newVal) {
      if (newVal === 'month') {
        /* istanbul ignore next */
        if (this.currentView !== 'year' || this.currentView !== 'month') {
          this.currentView = 'month';
        }
      } else if (newVal === 'dates') {
        this.currentView = 'date';
      }
    }
  },

  methods: {
    handleCancel () {
      this.visible = false;
      this.$emit('datetime-cancel', this.date, false);
    },
    _getValueIndex (value) {
      return value.length > 5
        ? { startIndex: POS_TIME_SECOND.start, endIndex: POS_TIME_SECOND.end }
        : { startIndex: POS_TIME_MINUTE.start, endIndex: POS_TIME_MINUTE.end };
    },
    proxyTimePickerDataProperties () {
      const format = timeFormat => {
        this.$refs.timepicker.format = timeFormat;
      };
      const value = _value => {
        this.$refs.timepicker.value = _value;
      };
      const date = _date => {
        this.$refs.timepicker.date = _date;
      };
      const selectableRange = _selectableRange => {
        this.$refs.timepicker.selectableRange = _selectableRange;
      };

      this.$watch('value', value);
      this.$watch('date', date);
      this.$watch('selectableRange', selectableRange);

      format(this.timeFormat);
      value(this.value);
      date(this.date);
      selectableRange(this.selectableRange);
    },

    handleClear () {
      this.date = this.getDefaultValue();
      this.$emit('pick', null);
    },

    emit (value, ...args) {
      if (!value) {
        this.$emit('pick', value, ...args);
      } else if (Array.isArray(value)) {
        const dates = value.map(date =>
          this.showTime ? clearMilliseconds(date) : clearTime(date)
        );
        this.$emit('pick', dates, ...args);
      } else {
        this.$emit(
          'pick',
          this.showTime ? clearMilliseconds(value) : clearTime(value),
          ...args
        );
      }
      this.userInputDate = null;
      this.userInputTime = null;
    },

    // resetDate() {
    //   this.date = new Date(this.date);
    // },

    showMonthPicker () {
      this.currentView = 'month';
    },

    showYearPicker () {
      this.currentView = 'year';
    },

    // XXX: 没用到
    // handleLabelClick() {
    //   if (this.currentView === 'date') {
    //     this.showMonthPicker();
    //   } else if (this.currentView === 'month') {
    //     this.showYearPicker();
    //   }
    // },

    prevMonth () {
      if (this.type === 'week') {
        this.keyboardDate = prevMonth(this.keyboardDate);
      } else {
        this.date = prevMonth(this.date || new Date());
      }
    },

    nextMonth () {
      if (this.type === 'week') {
        this.keyboardDate = nextMonth(this.keyboardDate);
      } else {
        this.date = nextMonth(this.date || new Date());
      }
    },

    prevYear () {
      if (this.currentView === 'year') {
        this.date = prevYear(this.date || new Date(), 10);
      } else {
        this.date = prevYear(this.date || new Date());
      }
    },

    nextYear () {
      if (this.currentView === 'year') {
        this.date = nextYear(this.date || new Date(), 10);
      } else {
        this.date = nextYear(this.date || new Date());
      }
    },

    handleShortcutClick (shortcut) {
      if (shortcut.onClick) {
        shortcut.onClick(this);
        this.$emit('shortcut', this);
      }
    },

    handleTimeCancel () {
      this.handleTimePickClose();
    },

    handleTimeConfirm (value, visible, first) {
      if (isDate(value)) {
        const newDate = this.date
          ? modifyTime(
            this.date,
            value.getHours(),
            value.getMinutes(),
            value.getSeconds()
          )
          : modifyWithTimeString(this.getDefaultValue(), this.defaultTime);
        this.date = newDate;
        this.emit(this.date, true);
      } else {
        this.emit(value, true);
      }
      if (!first) {
        this.timePickerVisible = visible;
      }
    },

    handleTimePickClose () {
      this.timePickerVisible = false;
    },

    handleMonthPick (month) {
      if (this.selectionMode === 'month') {
        this.date = modifyDate(this.date, this.year, month, 1);
        this.emit(this.date);
      } else {
        this.date = changeYearMonthAndClampDate(this.date, this.year, month);
        // should emit intermediate value ??
        // this.emit(this.date);
        this.currentView = 'date';
      }
    },

    handleDatePick (value) {
      if (this.selectionMode === 'day') {
        let newDate = this.value
          ? modifyDate(
            this.date,
            value.getFullYear(),
            value.getMonth(),
            value.getDate()
          )
          : modifyWithTimeString(value, this.defaultTime);
        // change default time while out of selectableRange
        if (!this.checkDateWithinRange(newDate)) {
          newDate = modifyDate(
            this.selectableRange[0][0],
            value.getFullYear(),
            value.getMonth(),
            value.getDate()
          );
        }
        let currentDate = new Date();
        newDate = new Date(
          newDate.getFullYear(),
          newDate.getMonth(),
          newDate.getDate(),
          currentDate.getHours(),
          currentDate.getMinutes(),
          currentDate.getSeconds()
        );
        this.keyboardDate = this.date = this.oldValue = newDate;

        this.emit(this.date, this.showTime);
      } else if (this.selectionMode === 'week') {
        this.emit(value.date);
      } else if (this.selectionMode === 'dates') {
        this.emit(value, true); // set false to keep panel open
      }
    },

    handleYearPick (year) {
      if (this.selectionMode === 'year') {
        this.date = modifyDate(this.date, year, 0, 1);
        this.emit(this.date);
      } else {
        this.date = changeYearMonthAndClampDate(this.date, year, this.month);
        // should emit intermediate value ??
        // this.emit(this.date, true);
        this.currentView = 'month';
      }
    },

    changeToNow () {
      // NOTE: not a permanent solution
      //       consider disable "now" button in the future
      if (
        (!this.disabledDate || !this.disabledDate(new Date())) &&
        this.checkDateWithinRange(new Date())
      ) {
        this.date = this.oldValue = new Date();
        if (!this.clickNowShowPicker) {
          this.emit(this.date, false);
        } else {
          this.emit(this.date, true);
        }
        this.$emit('change-now', this.date);
      }
    },

    confirm () {
      if (this.selectionMode === 'dates') {
        this.emit(this.value);
      } else {
        // value were emitted in handle{Date,Time}Pick, nothing to update here
        // deal with the scenario where: user opens the picker, then confirm without doing anything
        const value = this.visibleDate + ' ' + this.visibleTime;
        this.date = new Date(value); // refresh date
        this.emit(this.date);
      }
      this.$emit('date-confirm', this.date);
    },

    resetView () {
      if (this.selectionMode === 'month') {
        this.currentView = 'month';
      } else if (this.selectionMode === 'year') {
        this.currentView = 'year';
      } else {
        this.currentView = 'date';
      }
    },

    handleEnter () {
      document.body.addEventListener('keydown', this.handleKeydown);
    },

    handleLeave () {
      this.$emit('dodestroy');
      document.body.removeEventListener('keydown', this.handleKeydown);
    },

    handleFocus () {
      this.timeInputFocus = true;
    },

    handleBlur () {
      this.timeInputBlur = true;
    },

    // 时间日期选择器 键盘操作
    handleDatetimeKeydown (event) {
      const keyCode = event.keyCode;
      // Tab
      if (keyCode === 9) {
        this.timePickerVisible = false;
        if (this.timeInputBlur) {
          this.visible = false;
        }
        event.stopPropagation();
      }

      // Enter
      if (keyCode === 13) {
        if (this.isEnterKeyboard) {
          if (
            (this.date &&
              this.keyboardDate.getDate() === this.date.getDate()) ||
            (!this.date &&
              this.keyboardDate.getDate() === new Date().getDate())
          ) {
            this.collapseDatePicker = true;
          } else {
            this.collapseDatePicker = false;
          }
        }
        if (this.timeInputFocus) {
          this.timePickerVisible = !this.timePickerVisible;

          if (!this.timePickerVisible) {
            let time = modifyTime(
              this.date,
              this.$refs.timepicker.date.getHours(),
              this.$refs.timepicker.date.getMinutes(),
              this.$refs.timepicker.date.getSeconds()
            );
            this.emit(time, true);
            this.$refs.timepicker.handleConfirm(false, false, time);

            // 失去 timeInput 焦点
            this.refInput.forEach(input => {
              input.blur();
              this.collapseTimePicker = true;
            });
          }
          event.stopPropagation();
        } else if (this.collapseTimePicker || this.collapseDatePicker) {
          this.$emit('keydown', event);
          // 回车关闭 picker
          this.emit(this.keyboardDate);
          this.oldValue = this.date = this.keyboardDate;
          this.$emit('date-confirm', this.date);
        } else {
          this.timePickerVisible = false;
          this.handleDateKeydown(event, keyCode);
          this.emit(this.keyboardDate, true);
          this.oldValue = this.date = this.keyboardDate;
          event.preventDefault();
        }
      }
      if (
        keyCode === 37 ||
        keyCode === 38 ||
        keyCode === 39 ||
        keyCode === 40
      ) {
        if (this.timeInputFocus) {
          this.timePickerVisible = true;
          this.$refs.timepicker.handleKeydown(event);
          event.stopPropagation();
        } else {
          this.handleDateKeydown(event, keyCode);
          event.stopPropagation();
        }
      }
    },

    // 日期键盘操作
    handleDateKeydown (event, keyCode) {
      const list = [38, 40, 37, 39];
      if (this.visible && !this.timePickerVisible) {
        if (list.indexOf(keyCode) !== -1) {
          this.handleKeyControl(keyCode);
          event.stopPropagation();
          event.preventDefault();
        }
        if (
          keyCode === 13 &&
          this.userInputDate === null &&
          this.userInputTime === null
        ) {
          // Enter
          if (this.type !== 'datetime') {
            this.emit(this.keyboardDate, false);
          } else {
            this.emit(this.keyboardDate, true);
          }
        }
      }
    },

    // 键盘操作
    handleKeydown (event) {
      const keyCode = event.keyCode;

      if (this.type === 'datetime') {
        this.handleDatetimeKeydown(event);
        return;
      }
      // Tab
      if (keyCode === 9 && this.type !== 'datetime') {
        this.visible = false;
      }
      this.handleDateKeydown(event, keyCode);
    },

    handleKeyControl (keyCode) {
      const mapping = {
        year: {
          38: -4,
          40: 4,
          37: -1,
          39: 1,
          offset: (date, step) => date.setFullYear(date.getFullYear() + step)
        },
        month: {
          38: -4,
          40: 4,
          37: -1,
          39: 1,
          offset: (date, step) => date.setMonth(date.getMonth() + step)
        },
        week: {
          38: -1,
          40: 1,
          37: -1,
          39: 1,
          offset: (date, step) => date.setDate(date.getDate() + step * 7)
        },
        day: {
          38: -7,
          40: 7,
          37: -1,
          39: 1,
          offset: (date, step) => date.setDate(date.getDate() + step)
        }
      };
      const mode = this.selectionMode;
      const year = 3.1536e10;
      const now = this.keyboardDate.getTime();
      const newDate = new Date(this.keyboardDate.getTime());
      while (Math.abs(now - newDate.getTime()) <= year) {
        const map = mapping[mode];
        map.offset(newDate, map[keyCode]);
        if (
          typeof this.disabledDate === 'function' &&
          this.disabledDate(newDate)
        ) {
          continue;
        }
        this.keyboardDate = newDate;
        break;
      }
    },

    handleVisibleTimeChange (value) {
      const time = parseDate(value, this.timeFormat);
      if (time && this.checkDateWithinRange(time)) {
        this.date = modifyDate(time, this.year, this.month, this.monthDate);
        this.userInputTime = null;
        this.$refs.timepicker.value = this.date;
        this.emit(this.date, true);
      }
    },

    handleVisibleDateChange (value) {
      const date = parseDate(value, this.dateFormat);
      if (date) {
        if (
          typeof this.disabledDate === 'function' &&
          this.disabledDate(date)
        ) {
          return;
        }
        this.date = modifyTime(
          date,
          this.date.getHours(),
          this.date.getMinutes(),
          this.date.getSeconds()
        );
        this.userInputDate = null;
        this.resetView();
        this.emit(this.date, true);
      }
    },

    isValidValue (value) {
      return (
        value &&
        !isNaN(value) &&
        (typeof this.disabledDate === 'function'
          ? !this.disabledDate(value)
          : true) &&
        this.checkDateWithinRange(value)
      );
    },

    getDefaultValue () {
      // if default-value is set, return it
      // otherwise, return now (the moment this method gets called)
      let defaultValue = new Date();
      if (this.type === 'datetime') {
        defaultValue = this.currentDate;
      }

      return this.defaultValue ? new Date(this.defaultValue) : defaultValue;
    },

    checkDateWithinRange (date) {
      return this.selectableRange.length > 0
        ? timeWithinRange(date, this.selectableRange, this.format || 'HH:mm:ss')
        : true;
    }
  }
};
</script>



// WEBPACK FOOTER //
// date.vue?1246daf8